import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

interface UseSessionTimeoutOptions {
  // Time before session expires to show warning (in ms)
  warningTime?: number;
  // Absolute time since login after which the user is force-logged-out,
  // independent of the access token's own expiry/refresh cycle (in ms)
  maxLifetime?: number;
  // Callback when session is about to expire
  onWarning?: () => void;
  // Callback when the access token has expired
  onExpired?: () => void;
  // Callback when the absolute session lifetime has been exceeded
  onLifetimeExceeded?: () => void;
}

interface UseSessionTimeoutReturn {
  showWarning: boolean;
  timeRemaining: number;
  extendSession: () => Promise<void>;
  dismissWarning: () => void;
}

const DEFAULT_WARNING_TIME = 5 * 60 * 1000; // 5 minutes before expiry
const DEFAULT_MAX_LIFETIME = 12 * 60 * 60 * 1000; // 12 hours since login
const CHECK_INTERVAL = 30 * 1000; // Check every 30 seconds
const LOGIN_TIMESTAMP_KEY = 'ace-up-session-login-at';

const readStoredLoginAt = (): number | null => {
  if (typeof window === 'undefined') return null;
  try {
    const stored = window.localStorage.getItem(LOGIN_TIMESTAMP_KEY);
    if (!stored) return null;
    const parsed = Number(stored);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    // localStorage can be unavailable (e.g. Safari private browsing) or
    // unconfigured in some runtimes; treat as no stored login time.
    return null;
  }
};

const writeStoredLoginAt = (timestamp: number): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LOGIN_TIMESTAMP_KEY, String(timestamp));
  } catch {
    // Ignore write failures (e.g. private browsing or storage disabled).
  }
};

const clearStoredLoginAt = (): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(LOGIN_TIMESTAMP_KEY);
  } catch {
    // Ignore removal failures (e.g. private browsing or storage disabled).
  }
};

/**
 * Tracks session validity two ways at once, sharing one interval and one
 * logout path rather than duplicating either:
 *  - Token expiry: decodes the access token's `exp` claim and warns/expires
 *    as it approaches. With refresh-token rotation enabled (see
 *    EntryPoint.tsx), `getAccessTokenSilently()` transparently renews the
 *    token, so this rarely fires on its own.
 *  - Absolute lifetime: an absolute time-since-login cap (stored in
 *    localStorage), enforced regardless of how recently the token was
 *    refreshed. This is what actually bounds a session's total length.
 */
export const useSessionTimeout = (
  options: UseSessionTimeoutOptions = {}
): UseSessionTimeoutReturn => {
  const {
    warningTime = DEFAULT_WARNING_TIME,
    maxLifetime = DEFAULT_MAX_LIFETIME,
    onWarning,
    onExpired,
    onLifetimeExceeded,
  } = options;

  const { isAuthenticated, getAccessTokenSilently, logout } = useAuth0();
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [tokenExpiry, setTokenExpiry] = useState<number | null>(null);

  const warningShownRef = useRef(false);
  const expiredHandledRef = useRef(false);

  // Parse JWT to get expiry time
  const getTokenExpiry = useCallback((token: string): number | null => {
    try {
      const payload = token.split('.')[1];
      if (!payload) return null;
      const decoded = JSON.parse(atob(payload));
      return decoded.exp ? decoded.exp * 1000 : null; // Convert to milliseconds
    } catch {
      return null;
    }
  }, []);

  // Fetch token and determine expiry
  const checkTokenExpiry = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const token = await getAccessTokenSilently();
      const expiry = getTokenExpiry(token);
      setTokenExpiry(expiry);
    } catch (error) {
      console.error('Failed to get token:', error);
      // Token might be expired or invalid
      setTokenExpiry(Date.now());
    }
  }, [isAuthenticated, getAccessTokenSilently, getTokenExpiry]);

  // Single shared logout path for every kind of expiry (token or lifetime).
  const forceLogout = useCallback(() => {
    setShowWarning(false);
    clearStoredLoginAt();
    logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout]);

  // Extend session by refreshing token
  const extendSession = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently({ cacheMode: 'off' });
      const expiry = getTokenExpiry(token);
      setTokenExpiry(expiry);
      setShowWarning(false);
      warningShownRef.current = false;
      expiredHandledRef.current = false;
    } catch (error) {
      console.error('Failed to extend session:', error);
      // If refresh fails, log the user out
      forceLogout();
    }
  }, [getAccessTokenSilently, getTokenExpiry, forceLogout]);

  // Dismiss warning (user acknowledges but doesn't extend)
  const dismissWarning = useCallback(() => {
    setShowWarning(false);
  }, []);

  // Stamp (or clear) the absolute-lifetime login timestamp as auth state
  // changes. Kept separate from the interval effect below since it's a
  // distinct concern (recording login) from periodic checking.
  useEffect(() => {
    if (isAuthenticated) {
      expiredHandledRef.current = false;
      if (readStoredLoginAt() === null) {
        writeStoredLoginAt(Date.now());
      }
    } else {
      clearStoredLoginAt();
    }
  }, [isAuthenticated]);

  // Check session status periodically: absolute lifetime first, then token
  // expiry, on one shared interval.
  useEffect(() => {
    if (!isAuthenticated) {
      setShowWarning(false);
      setTimeRemaining(0);
      setTokenExpiry(null);
      return;
    }

    // Returns true if the absolute lifetime was exceeded (and logout fired).
    const checkLifetime = (): boolean => {
      const loginAt = readStoredLoginAt();
      if (loginAt === null || expiredHandledRef.current) return false;
      if (Date.now() - loginAt < maxLifetime) return false;

      expiredHandledRef.current = true;
      onLifetimeExceeded?.();
      forceLogout();
      return true;
    };

    if (checkLifetime()) return;

    // Initial token check
    checkTokenExpiry();

    const interval = setInterval(() => {
      if (checkLifetime()) return;

      if (tokenExpiry === null) {
        checkTokenExpiry();
        return;
      }

      const now = Date.now();
      const remaining = tokenExpiry - now;
      setTimeRemaining(Math.max(0, remaining));

      // Show warning when approaching expiry
      if (remaining <= warningTime && remaining > 0 && !warningShownRef.current) {
        setShowWarning(true);
        warningShownRef.current = true;
        onWarning?.();
      }

      // Handle expiry
      if (remaining <= 0 && !expiredHandledRef.current) {
        expiredHandledRef.current = true;
        onExpired?.();
        forceLogout();
      }
    }, CHECK_INTERVAL);

    return () => clearInterval(interval);
  }, [
    isAuthenticated,
    tokenExpiry,
    warningTime,
    maxLifetime,
    checkTokenExpiry,
    onWarning,
    onExpired,
    onLifetimeExceeded,
    forceLogout,
  ]);

  // Also check on visibility change (user returns to tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isAuthenticated) {
        checkTokenExpiry();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuthenticated, checkTokenExpiry]);

  return {
    showWarning,
    timeRemaining,
    extendSession,
    dismissWarning,
  };
};

export default useSessionTimeout;
